import { Component, createRef } from 'react';
import { withTranslation } from 'react-i18next';
import html2canvas from 'html2canvas';
import moment from 'moment';

import { scrollToBlock } from '../../../helpers/scrollToBlock';

// The Search Console property list has one implementation in the app, on the tool that introduced
// it; every GSC tool picks a property out of everything Search Console exposes to the user, which
// is a wider list than the domains linked to GDDash.
import { getUserDomains } from '../../DomainPerformance/query';
import ScopeControls from '../GscTools/components/ScopeControls';
import CoverageStrip from '../GscTools/components/CoverageStrip';
import { ELIGIBLE_PERMISSIONS, probeLatestDate } from '../GscTools/query';
import { downloadCsv } from '../GscTools/csv';
import {
    buildSeries,
    coverageFor,
    hasDuplicateKeys,
    joinRows,
    reportsPosition,
    sumRows,
} from '../GscTools/metrics';
import { DETAIL_MAX_DAYS, defaultPeriod, formatPeriod, periodEndingOn } from '../GscTools/period';
import { readParams, writeParams } from '../GscTools/url';

import AppliedFilters from './components/AppliedFilters';
import Contributors from './components/Contributors';
import ExtraControls from './components/ExtraControls';
import Retrieval from './components/Retrieval';
import RuleEditor from './components/RuleEditor';
import RowsTable from './components/RowsTable';
import SelectedRowTrend from './components/SelectedRowTrend';
import { buildRowsCsv } from './exportRows';
import { fetchContributors, fetchKeyedChunk, fetchSelectedRowSeries } from './query';
import { applyPreset, orderRows, retrievalState } from './rows';
import {
    URL_KEYS,
    hasReportScope,
    initialDraft,
    reportDraftFromParams,
    reportParamsFromScope,
    resolveReportScope,
} from './scope';

const IDLE_ROWS = {
    status: 'idle',
    joined: [],
    currentChunk: null,
    priorChunk: null,
    error: null,
    loadingMore: false,
};

const IDLE_DETAIL = { status: 'idle', error: null };

/**
 * Pages, Queries & Changes — one Search Console property's retrieved pages or queries across two
 * periods, with the contributors and the daily history of whichever row is selected.
 *
 * The report is four requests deep and every one of them is explicit. Applying a scope fetches
 * the keyed rows for both periods, independently, so a baseline that fails leaves the current
 * period's rows standing. Selecting a row then fetches that row's contributors and its two daily
 * series — and nothing before that, because a contributor list for a row nobody has selected is a
 * request nobody asked for.
 *
 * Two kinds of staleness are guarded separately. A scope generation is bumped by every apply and
 * refresh, so a slow answer to a scope the user has moved on from is dropped rather than painted
 * over the newer one; a detail generation is bumped by every selection and by every apply, so the
 * previous row's evidence can never appear under the current row's heading, and switching the
 * view clears the selection with the rows it belonged to.
 *
 * Nothing fetched here is persisted. The data lives in this component's state for as long as the
 * page is open, and the export in block 05 builds its file from that same memory. The scope is the
 * one exception: it is kept in the page's address — its rules among it — so a reload or a shared
 * link opens the same report rather than an empty scope bar, and a report found in the URL is run
 * on arrival.
 */
class PagesQueriesChanges extends Component {
    constructor(props) {
        super(props);

        this.handleDraftChange = this.handleDraftChange.bind(this);
        this.handleRulesChange = this.handleRulesChange.bind(this);
        this.handleApply = this.handleApply.bind(this);
        this.handleRefresh = this.handleRefresh.bind(this);
        this.handleToggleControls = this.handleToggleControls.bind(this);
        this.handleSelectRow = this.handleSelectRow.bind(this);
        this.handleMode = this.handleMode.bind(this);
        this.handleLoadMore = this.handleLoadMore.bind(this);
        this.handleExportRows = this.handleExportRows.bind(this);
        this.handlePrint = this.handlePrint.bind(this);
        this.handleDownloadImage = this.handleDownloadImage.bind(this);

        this.credentialsDomainId = props.credentials_domain_id || '';
        this.reportRef = createRef();
        // The two blocks a picked row feeds, so the first pick can bring them into view.
        this.detailsRef = createRef();

        this.generation = 0;
        this.detailGeneration = 0;
        this.isMounted_ = false;

        const period = defaultPeriod(DETAIL_MAX_DAYS);

        // The address is read once, here, so the controls show the scope it names from the first
        // paint rather than flicking from the defaults to it a moment later.
        const params = readParams();
        this.urlDraft = reportDraftFromParams(params);
        this.urlHasScope = hasReportScope(params);

        this.state = {
            properties: [],
            loadingProperties: true,
            propertiesError: null,
            // The property's most recent day with final data, once probed for.
            latestDate: '',

            draft: { ...initialDraft(period), ...this.urlDraft },

            applied: null,
            loading: false,
            fetchedAt: null,
            controlsOpen: true,
            capturing: false,

            rows: { ...IDLE_ROWS },
            selectedKey: null,
            contributors: { ...IDLE_DETAIL, rows: [], totals: null },
            trend: { ...IDLE_DETAIL, current: null, baseline: null },
        };
    }

    async componentDidMount() {
        this.isMounted_ = true;

        let properties = [];
        let propertiesError = null;

        try {
            const sites = await getUserDomains();
            properties = sites.filter((site) => ELIGIBLE_PERMISSIONS.has(site.permissionLevel));
        } catch (err) {
            console.log(err);
            propertiesError = err?.message ?? this.props.t('global.something_went_wrong');
        }

        if (!this.isMounted_) return;

        // A link naming a property this account cannot read is not quietly answered with another
        // property's figures: the first one is selected and the scope bar is left open for the
        // user to confirm, rather than a report being run under a scope nobody asked for.
        const requested = this.urlDraft.siteUrl
            ? properties.find((item) => item.siteUrl === this.urlDraft.siteUrl)
            : null;
        const property = requested ?? properties[0];
        const autoApply = this.urlHasScope && !!property && (!this.urlDraft.siteUrl || !!requested);

        this.setState(
            (state) => ({
                properties,
                propertiesError,
                loadingProperties: false,
                draft: property
                    ? {
                          ...state.draft,
                          siteUrl: property.siteUrl,
                          permissionLevel: property.permissionLevel,
                      }
                    : state.draft,
            }),
            () => {
                // Dates that came from the address are the user's own: the probe still runs, for
                // the preset anchor, but it does not move them.
                if (property) {
                    this.suggestDates(property.siteUrl, { seedDates: !this.urlDraft.startDate });
                }
                if (autoApply) this.handleApply();
            },
        );
    }

    componentWillUnmount() {
        this.isMounted_ = false;
    }

    /**
     * Seeds the date controls from the property's most recent activity, so the window the user is
     * offered has something in it rather than ending in days Search Console has not filled yet.
     *
     * This is a suggestion, not a watermark: it moves the draft dates only, costs one small
     * request, and is abandoned the moment the user edits them.
     */
    async suggestDates(siteUrl, { seedDates = true } = {}) {
        if (!this.credentialsDomainId || !siteUrl) return;

        try {
            const latest = await probeLatestDate({
                credentialsDomainId: this.credentialsDomainId,
                siteUrl,
                searchType: this.state.draft.searchType,
            });

            if (!latest || !this.isMounted_) return;

            this.setState((state) => {
                if (state.draft.siteUrl !== siteUrl) return null;
                // Kept as well as applied: the scope controls count their date presets back from
                // this day, so one click lands on days the property has data for.
                if (!seedDates) return { latestDate: latest };

                return {
                    latestDate: latest,
                    draft: { ...state.draft, ...periodEndingOn(latest, DETAIL_MAX_DAYS) },
                };
            });
        } catch (err) {
            // A probe that fails costs nothing: the controls keep their default window.
            console.log(err);
        }
    }

    scope() {
        return resolveReportScope(this.state.draft, this.props.t);
    }

    handleDraftChange(patch) {
        this.setState(
            (state) => ({ draft: { ...state.draft, ...patch } }),
            () => {
                if (patch.siteUrl) {
                    const property = this.state.properties.find(
                        (item) => item.siteUrl === patch.siteUrl,
                    );
                    this.setState(
                        (state) => ({
                            draft: {
                                ...state.draft,
                                permissionLevel: property?.permissionLevel ?? '',
                            },
                        }),
                        () => this.suggestDates(patch.siteUrl),
                    );
                }
            },
        );
    }

    handleRulesChange(rules) {
        this.setState((state) => ({ draft: { ...state.draft, rules } }));
    }

    handleApply() {
        const scope = this.scope();
        if (scope.errors.length) return;
        this.load(scope);
    }

    handleRefresh() {
        if (this.state.applied) this.load(this.state.applied);
    }

    handleToggleControls() {
        this.setState((state) => ({ controlsOpen: !state.controlsOpen }));
    }

    /**
     * Re-orders the rows already retrieved.
     *
     * Mode and the metric it reads are applied in memory, so this never costs a request — which is
     * also why the applied scope is updated alongside the draft: the order the table is in has to
     * be the order the block header and the export claim it is in.
     */
    handleMode(mode) {
        this.setState((state) => ({
            draft: { ...state.draft, mode },
            applied: state.applied ? { ...state.applied, mode } : state.applied,
        }));
    }

    /**
     * Runs the applied scope: one keyed request per period, both in flight at once, each landing
     * on its own.
     *
     * Any row selected under the previous scope is dropped with the rows it belonged to. Keeping
     * it would leave blocks 03 and 04 showing one scope's evidence beneath another scope's table.
     */
    load(scope) {
        this.generation += 1;
        this.detailGeneration += 1;
        const generation = this.generation;

        // Written here rather than in handleApply so the address also follows a refresh, and so
        // it only ever describes a scope that was actually run.
        writeParams(reportParamsFromScope(scope), URL_KEYS);

        this.setState({
            applied: scope,
            controlsOpen: false,
            loading: true,
            fetchedAt: new Date().toISOString(),
            rows: { ...IDLE_ROWS, status: 'loading' },
            selectedKey: null,
            contributors: { ...IDLE_DETAIL, rows: [], totals: null },
            trend: { ...IDLE_DETAIL, current: null, baseline: null },
        });

        this.loadRows(scope, generation, { more: false });
    }

    /**
     * Fetches one chunk of keyed rows for each period and joins them on the exact key.
     *
     * The two periods keep independent cursors: Search Console can hand back a full chunk for one
     * window and a short one for the other, and advancing them together would either re-read rows
     * or skip them. Duplicate keys inside one period stop that period rather than being counted
     * twice.
     */
    async loadRows(scope, generation, { more }) {
        const previousCurrent = more ? this.state.rows.currentChunk : null;
        const previousPrior = more ? this.state.rows.priorChunk : null;

        this.setState((state) => ({
            rows: {
                ...state.rows,
                status: more ? state.rows.status : 'loading',
                loadingMore: more,
                error: null,
            },
        }));

        const request = (period, chunk) => {
            if (!period) return Promise.resolve(null);
            if (more && (!chunk || chunk.nextOffset === null)) return Promise.resolve(chunk);

            return fetchKeyedChunk({
                credentialsDomainId: this.credentialsDomainId,
                scope,
                period,
                offset: more ? chunk.nextOffset : 0,
            }).then((result) => {
                const rows = [...(chunk?.rows ?? []), ...result.rows];
                if (hasDuplicateKeys(rows))
                    throw new Error(this.props.t('tools.gsc.duplicate_rows'));
                return { ...result, rows };
            });
        };

        try {
            const [currentChunk, priorChunk] = await Promise.all([
                request(scope.current, previousCurrent),
                request(scope.baseline, previousPrior),
            ]);

            if (!this.isMounted_ || generation !== this.generation) return;

            this.setState({
                loading: false,
                rows: {
                    status: 'done',
                    loadingMore: false,
                    error: null,
                    currentChunk,
                    priorChunk,
                    joined: joinRows(
                        currentChunk?.rows,
                        priorChunk?.rows,
                        reportsPosition(scope.searchType),
                    ),
                },
            });
        } catch (err) {
            console.log('rows', err);
            if (!this.isMounted_ || generation !== this.generation) return;

            this.setState((state) => ({
                loading: false,
                rows: {
                    ...state.rows,
                    // A failure while loading more keeps the rows already joined; a failure on the
                    // first chunk has nothing to keep.
                    status: state.rows.joined.length ? 'done' : 'error',
                    loadingMore: false,
                    error: err?.message ?? this.props.t('global.something_went_wrong'),
                },
            }));
        }
    }

    handleLoadMore() {
        const scope = this.state.applied;
        if (scope) this.loadRows(scope, this.generation, { more: true });
    }

    /**
     * Selecting a row is what blocks 03 and 04 are for, so it fetches both: the row's
     * contributors in the current window, and its daily series in each window.
     *
     * The detail generation is bumped first, which is what makes the previous selection's answers
     * unusable the moment a new row is picked — a slow contributor response cannot land under the
     * wrong heading. Selecting the row that is already selected clears it instead, which is how
     * the table's single-select toggle behaves.
     */
    handleSelectRow(key) {
        const scope = this.state.applied;
        this.detailGeneration += 1;
        const detailGeneration = this.detailGeneration;

        if (!key || !scope) {
            return this.setState({
                selectedKey: null,
                contributors: { ...IDLE_DETAIL, rows: [], totals: null },
                trend: { ...IDLE_DETAIL, current: null, baseline: null },
            });
        }

        this.setState(
            {
                selectedKey: key,
                contributors: { status: 'loading', error: null, rows: [], totals: null },
                trend: { status: 'loading', error: null, current: null, baseline: null },
            },
            // Every pick brings the blocks into view, not just the first. They sit below the
            // fold, so a pick that left the page where it was would look like nothing happened —
            // and that is as true of the fifth row as of the first. scrollToBlock moves the page
            // only when the block is not already readable, so a reader already down there is
            // left alone.
            () => scrollToBlock(this.detailsRef.current),
        );

        this.loadContributors(scope, key, detailGeneration);
        this.loadTrend(scope, key, detailGeneration);
    }

    async loadContributors(scope, key, detailGeneration) {
        try {
            const rows = await fetchContributors({
                credentialsDomainId: this.credentialsDomainId,
                scope,
                period: scope.current,
                selectedKey: key,
            });

            if (!this.isMounted_ || detailGeneration !== this.detailGeneration) return;

            const hasPosition = reportsPosition(scope.searchType);

            this.setState({
                contributors: {
                    status: 'done',
                    error: null,
                    rows: rows.map((row) => ({
                        id: row?.keys?.[0],
                        key: row?.keys?.[0],
                        clicks: row?.clicks ?? 0,
                        impressions: row?.impressions ?? 0,
                        ctr: (row?.impressions ?? 0) > 0 ? row?.ctr ?? 0 : null,
                        position:
                            (row?.impressions ?? 0) > 0 && hasPosition ? row?.position ?? 0 : null,
                    })),
                    totals: sumRows(rows, hasPosition),
                },
            });
        } catch (err) {
            console.log('contributors', err);
            if (!this.isMounted_ || detailGeneration !== this.detailGeneration) return;

            this.setState({
                contributors: {
                    status: 'error',
                    error: err?.message ?? this.props.t('global.something_went_wrong'),
                    rows: [],
                    totals: null,
                },
            });
        }
    }

    /**
     * The selected row's daily series for each period.
     *
     * The sums shown on the cards come from the days that were returned, through the same
     * `sumRows` the contributor block uses, so a CTR card is clicks over impressions and never the
     * mean of the days' own CTRs.
     */
    async loadTrend(scope, key, detailGeneration) {
        const hasPosition = reportsPosition(scope.searchType);

        const forPeriod = async (period) => {
            if (!period) return null;
            const daily = await fetchSelectedRowSeries({
                credentialsDomainId: this.credentialsDomainId,
                scope,
                period,
                selectedKey: key,
            });

            return {
                series: buildSeries(daily, period, hasPosition),
                coverage: coverageFor(daily, period),
                totals: sumRows(daily, hasPosition),
            };
        };

        try {
            const [current, baseline] = await Promise.all([
                forPeriod(scope.current),
                forPeriod(scope.baseline),
            ]);

            if (!this.isMounted_ || detailGeneration !== this.detailGeneration) return;

            this.setState({ trend: { status: 'done', error: null, current, baseline } });
        } catch (err) {
            console.log('trend', err);
            if (!this.isMounted_ || detailGeneration !== this.detailGeneration) return;

            this.setState({
                trend: {
                    status: 'error',
                    error: err?.message ?? this.props.t('global.something_went_wrong'),
                    current: null,
                    baseline: null,
                },
            });
        }
    }

    /** The rows as the table shows them: the preset applied, then the mode's order. */
    visibleRows() {
        const scope = this.state.applied;
        if (!scope) return [];
        return orderRows(applyPreset(this.state.rows.joined, scope), scope);
    }

    exportName(prefix) {
        const scope = this.state.applied;
        return `${prefix}_${scope?.view ?? 'rows'}_${scope?.siteUrl ?? 'property'}_${
            scope?.current?.startDate ?? ''
        }`
            .replace(/[^a-z0-9_.-]+/gi, '-')
            .toLowerCase();
    }

    handleExportRows() {
        const scope = this.state.applied;
        if (!scope || this.state.rows.status !== 'done') return;

        downloadCsv(
            buildRowsCsv({
                t: this.props.t,
                scope,
                // Everything retrieved, in the order on screen — not the page of rows the table
                // happens to be showing.
                rows: this.visibleRows(),
                fetchedAt: this.state.fetchedAt
                    ? moment(this.state.fetchedAt).format('YYYY-MM-DD HH:mm')
                    : null,
                retrieval: retrievalState(this.state.rows.currentChunk, this.state.rows.priorChunk),
            }),
            `${this.exportName('pages-queries-changes')}.csv`,
        );
    }

    handlePrint() {
        window.print();
    }

    /**
     * Captures the loaded report as an image, from the DOM already on screen — no further request
     * and no upload. The controls that produced the report and the export actions themselves are
     * left out of the frame, while the scope and coverage labels stay in it, so the picture still
     * says what it is a picture of.
     */
    async handleDownloadImage() {
        const container = this.reportRef.current;
        if (!container) return;

        this.setState({ capturing: true });

        try {
            const canvas = await html2canvas(container, {
                backgroundColor: '#ffffff',
                scale: 2,
                ignoreElements: (element) =>
                    element.classList?.contains('gsc-tool-controls') ||
                    element.classList?.contains('gsc-tool-controls-bottom') ||
                    element.classList?.contains('gsc-tool-rules') ||
                    element.classList?.contains('gsc-tool-page-actions') ||
                    // The shared chart and table carry their own refresh and download buttons.
                    element.classList?.contains('chart-action-btns') ||
                    element.classList?.contains('dt-actions__bar'),
            });

            const link = document.createElement('a');
            link.href = canvas.toDataURL('image/png');
            link.download = `${this.exportName('pages-queries-changes')}.png`;
            link.click();
        } catch (err) {
            console.log(err);
        } finally {
            if (this.isMounted_) this.setState({ capturing: false });
        }
    }

    render() {
        const { t } = this.props;
        const scope = this.scope();
        const applied = this.state.applied;
        const rows = this.visibleRows();
        const retrieval = retrievalState(this.state.rows.currentChunk, this.state.rows.priorChunk);

        const presetHidden = applied?.presetApplies
            ? this.state.rows.joined.length - applyPreset(this.state.rows.joined, applied).length
            : 0;

        const selectedRow = this.state.selectedKey
            ? this.state.rows.joined.find((row) => row.key === this.state.selectedKey)
            : null;

        return (
            <div className="gsc-tool" ref={this.reportRef}>
                <div className="gsc-tool-title-row">
                    <div>
                        <h2 className="page-title">{t('tools.pages_queries_changes.header')}</h2>
                        <p className="gsc-tool-sub">{t('tools.pages_queries_changes.subtitle')}</p>
                    </div>

                    <div className="gsc-tool-page-actions">
                        <button
                            type="button"
                            className="button button-outline button-primary btn"
                            onClick={this.handleDownloadImage}
                            disabled={this.state.rows.status !== 'done' || this.state.capturing}
                        >
                            <i className="icon-fi_download" />
                            {this.state.capturing
                                ? t('tools.gsc.exports.capturing')
                                : t('tools.gsc.exports.image')}
                        </button>
                        <button
                            type="button"
                            className="button button-outline button-primary btn"
                            onClick={this.handlePrint}
                            disabled={this.state.rows.status !== 'done'}
                        >
                            <i className="icon-fi_download" />
                            {t('tools.gsc.exports.full_page')}
                        </button>
                    </div>
                </div>

                {!this.credentialsDomainId && (
                    <div className="gsc-tool-banner error">
                        {t('tools.gsc.controls.no_credentials')}
                    </div>
                )}

                <ScopeControls
                    draft={this.state.draft}
                    scope={scope}
                    applied={applied}
                    open={this.state.controlsOpen}
                    properties={this.state.properties}
                    loadingProperties={this.state.loadingProperties}
                    propertiesError={this.state.propertiesError}
                    latestDate={this.state.latestDate}
                    loading={this.state.loading}
                    maxDays={DETAIL_MAX_DAYS}
                    onChange={this.handleDraftChange}
                    onApply={this.handleApply}
                    onRefresh={this.handleRefresh}
                    onToggle={this.handleToggleControls}
                    extraScopeLabels={[
                        t(`tools.pages_queries_changes.views.${scope.view}`),
                        t(`tools.pages_queries_changes.modes.${scope.mode}`),
                    ]}
                    footer={
                        <RuleEditor
                            rules={this.state.draft.rules}
                            onChange={this.handleRulesChange}
                        />
                    }
                >
                    <ExtraControls draft={this.state.draft} onChange={this.handleDraftChange} />
                </ScopeControls>

                {!applied ? (
                    <div className="card table-main rounded-main overflow-hidden gsc-tool-block">
                        <div className="card-body gsc-tool-load-state">
                            <span className="gsc-tool-load-icon" aria-hidden="true">
                                <i className="icon-pages" />
                            </span>
                            <h4>{t('tools.pages_queries_changes.idle_title')}</h4>
                            <p>
                                {t('tools.pages_queries_changes.idle', {
                                    days: DETAIL_MAX_DAYS,
                                })}
                            </p>
                        </div>
                    </div>
                ) : (
                    <>
                        <CoverageStrip
                            scope={applied}
                            current={{
                                status: this.state.rows.status,
                                coverage: this.state.trend.current?.coverage ?? null,
                                error: this.state.rows.error,
                            }}
                            baseline={
                                applied.baseline
                                    ? {
                                          status: this.state.rows.status,
                                          coverage: this.state.trend.baseline?.coverage ?? null,
                                          error: this.state.rows.error,
                                      }
                                    : null
                            }
                            fetchedAt={this.state.fetchedAt}
                            statusNote={t('tools.pages_queries_changes.coverage_note')}
                        />

                        <AppliedFilters scope={applied} />

                        <RowsTable
                            scope={applied}
                            status={this.state.rows.status}
                            error={this.state.rows.error}
                            rows={rows}
                            hiddenByPreset={presetHidden}
                            selectedKey={this.state.selectedKey}
                            stopReason={retrieval.stopReason}
                            loadingMore={this.state.rows.loadingMore}
                            onLoadMore={this.handleLoadMore}
                            onSelect={this.handleSelectRow}
                            onMode={this.handleMode}
                        />

                        {/* The two blocks a picked row fills, wrapped so the first pick can
                            bring them into view. */}
                        <div ref={this.detailsRef}>
                            <Contributors
                                scope={applied}
                                status={this.state.contributors.status}
                                error={this.state.contributors.error}
                                rows={this.state.contributors.rows}
                                totals={this.state.contributors.totals}
                                selectedKey={this.state.selectedKey}
                                selectedRow={selectedRow}
                                period={applied.current}
                            />

                            <SelectedRowTrend
                                scope={applied}
                                status={this.state.trend.status}
                                error={this.state.trend.error}
                                selectedKey={this.state.selectedKey}
                                current={this.state.trend.current}
                                baseline={this.state.trend.baseline}
                                metric={applied.metric}
                            />
                        </div>

                        <Retrieval
                            scope={applied}
                            status={this.state.rows.status}
                            retrieval={retrieval}
                            rowCount={this.state.rows.joined.length}
                            exportable={this.state.rows.joined.length > 0}
                            onExport={this.handleExportRows}
                            fetchedAt={
                                this.state.fetchedAt
                                    ? moment(this.state.fetchedAt).format('DD MMM YYYY, HH:mm')
                                    : null
                            }
                        />

                        <p className="gsc-tool-table-note">
                            {t('tools.pages_queries_changes.footnote', {
                                current: formatPeriod(applied.current),
                                baseline: formatPeriod(applied.baseline),
                            })}
                        </p>
                    </>
                )}
            </div>
        );
    }
}

export default withTranslation()(PagesQueriesChanges);
